import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { get } from '../../../redux/api';
import type { Organization } from '../../../types';

type MinOrg = Pick<Organization, 'id' | 'title' | 'access_level'>;

const fetchOrganization = async (organizationId: number): Promise<MinOrg> => {
  const response = await get<Organization>(`/api/organizations/${organizationId}`);
  const org = response.data;
  return {
    id: org?.id ?? 0,
    title: org?.title ?? '',
    access_level: org?.access_level ?? 'free',
  };
};

/**
 * Hook to fetch multiple organizations by their IDs.
 * Uses parallel queries to fetch each organization individually.
 * @param orgIds - Array of organization IDs to fetch
 * @returns Combined loading state and array of organizations
 */
export const usePinnedOrganizationsData = (orgIds: number[]) => {
  const queries = useQueries({
    queries: orgIds.map((orgId) => ({
      queryKey: ['organization', orgId],
      queryFn: () => fetchOrganization(orgId),
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  const { organizations, isLoading } = useMemo(() => {
    const isAnyLoading = queries.some((query) => query.isLoading);
    const orgs = queries
      .filter((query) => query.data)
      .map((query) => query.data as MinOrg)
      .sort((a, b) => a.title?.localeCompare(b.title ?? '') ?? 0); // Sort alphabetically

    return {
      organizations: orgs,
      isLoading: isAnyLoading,
    };
  }, [queries]);

  return {
    organizations,
    isLoading,
  };
};
